<template>
  <div class="bg-base-100 p-4 px-4 rounded-lg flex flex-col">
    <div class="flex justify-between items-center mb-2">
      <div class="flex items-center gap-2">
        <h3 class="text-lg font-semibold">操作历史</h3>
      </div>
      <button class="btn btn-ghost btn-sm" @click="handleClearHistory">
        <i class="iconify mingcute--delete-2-line" />
      </button>
    </div>
    <div :class="{ hidden: isCollapsed }">
      <!-- Add tabs -->
      <div class="tabs tabs-boxed bg-base-200">
        <a
          class="tab flex items-center justify-center"
          :class="{ 'tab-active': selectedTab === 'all' }"
          @click="selectedTab = 'all'"
        >
          <span class="text-xs">全部</span>
        </a>
        <a
          class="tab flex items-center justify-center"
          :class="{ 'tab-active': selectedTab === 'generate' }"
          @click="selectedTab = 'generate'"
        >
          <span class="text-xs">辅助绘图</span>
        </a>
        <a
          class="tab flex items-center justify-center"
          :class="{ 'tab-active': selectedTab === 'draw' }"
          @click="selectedTab = 'draw'"
        >
          <span class="text-xs">手绘</span>
        </a>
      </div>
      <div
        class="text-sm text-base-content/70 rounded-lg p-2 max-w-xs max-h-[320px] overflow-y-auto overflow-x-hidden flex flex-col gap-2 items-center"
      >
        <div>这里展示你最近{{ store.MAX_HISTORY_LENGTH }}次的操作记录哦！</div>
      </div>
      <div v-if="filteredHistory.length > 0" class="overflow-y-auto h-[80vh]">
        <div
          v-for="entry in filteredHistory"
          :key="entry.timestamp"
          class="flex items-start gap-2 p-2 hover:bg-base-200 rounded cursor-pointer transition-colors"
          :class="{ 'bg-primary/10': selectedHistoryId === entry.timestamp }"
          @click="loadHistoryEntry(entry)"
        >
          <div v-if="entry.imageUrl" class="relative w-16 h-16">
            <img
              :src="entry.imageUrl"
              alt="Generated Image"
              class="w-full h-full object-cover rounded"
              crossorigin="anonymous"
            />
          </div>
          <div
            v-else
            class="text-xs text-base-content/70 flex items-center gap-2"
          >
            <span class="font-medium">{{ entry.tool || "Created" }}</span>
            <span>{{ formatTimestamp(entry.timestamp) }}</span>
          </div>
          <div class="flex-1">
            <template v-if="entry.imageUrl">
              <div class="text-xs text-base-content/70">
                {{ formatTimestamp(entry.timestamp) }}
              </div>
              <p class="text-sm max-h-[60px] overflow-hidden text-ellipsis">
                {{ entry.prompt }}
              </p>
              <div class="text-xs text-base-content/70 mt-1">
                <span>Strength: {{ entry.strength }}</span>
                <span class="mx-2">|</span>
                <span>Line Weight: {{ entry.lineWeight }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-gray-500 py-4">
        这里是你的操作历史记录，目前还没有任何记录哦！
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useAvatarStore } from "@/features/draw/stores/useAvatarStore";
import { useImageProcessing } from "@/features/draw/composables/useImageProcessing";
import { clearHistory } from "@/features/draw/utils/indexedDB";
import { CANVAS_SIZE } from "@/features/draw/constants/canvas";

const emit = defineEmits<{
  (
    e: "generate-layer",
    data: { layerId: number; pixels: Uint32Array; forceAddToHistory?: boolean }
  ): void;
  (e: "merge-colors", threshold: number, forceAddToHistory?: boolean): void;
  (e: "update-dynamic-prompt", prompt: string): void;
  (e: "update:strength", value: number): void;
  (e: "update:lineWeight", value: number): void;
  (e: "set-view-mode", mode: "pixel" | "raw"): void;
}>();

const props = defineProps<{
  selectedLayer: number;
}>();

// Add tab state
const selectedTab = ref("all");
const selectedHistoryId = ref<number | null>(null);

// Add collapsed state
const isCollapsed = ref(false);

// 使用Pinia store
const store = useAvatarStore();

// Sort history by timestamp in descending order (newest first)
const sortedHistory = computed(() => {
  return [...store.history].sort((a, b) => b.timestamp - a.timestamp);
});

// Filter history based on selected tab
const filteredHistory = computed(() => {
  if (selectedTab.value === "all") {
    return sortedHistory.value;
  } else if (selectedTab.value === "generate") {
    return sortedHistory.value.filter((entry) => entry.imageUrl);
  } else {
    return sortedHistory.value.filter((entry) => !entry.imageUrl);
  }
});

const loadHistoryEntry = async (entry: (typeof store.history)[0]) => {
  store.currentHistoryIndex = store.history.findIndex(
    (h) => h.timestamp === entry.timestamp
  );
  selectedHistoryId.value = entry.timestamp;
  // 更新提示词
  emit("update-dynamic-prompt", entry.extractedPrompt || "");

  // 更新参数
  emit("update:strength", entry.strength || 0.5);
  emit("update:lineWeight", entry.lineWeight || 0.5);

  // 自动切换到Edit View模式，这样用户可以看到加载的像素图
  emit("set-view-mode", "pixel");

  // 如果是生成的图片，则加载图片
  if (entry.imageUrl) {
    try {
      const { createImageFromUrl } = useImageProcessing();
      // The imageUrl is now a base64 string, which is persistent across sessions
      const processedImage = await createImageFromUrl(entry.imageUrl);

      // 恢复图层状态，不创建新的历史记录
      emit("generate-layer", {
        layerId: props.selectedLayer,
        pixels: processedImage.pixels,
        forceAddToHistory: false,
      });

      // 合并颜色时也不创建新的历史记录
      // emit("merge-colors", 5, false);
    } catch (error) {
      console.error("Error loading history image:", error);
    }
  } else {
    // 如果是画布状态，直接恢复图层状态
    emit("generate-layer", {
      layerId: props.selectedLayer,
      pixels:
        entry.layers.find((l) => l.id === props.selectedLayer)?.pixels ||
        new Uint32Array(CANVAS_SIZE * CANVAS_SIZE),
      forceAddToHistory: false,
    });
  }
};

const formatTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const handleClearHistory = async () => {
  // 显示确认对话框
  if (!window.confirm("确定要清除所有历史记录吗？此操作无法撤消。")) {
    return;
  }

  try {
    // 清除内存中的历史记录
    store.history = store.initHistory();
    selectedHistoryId.value = null;

    // 清除 IndexedDB 中的历史记录
    await clearHistory();
  } catch (error) {
    console.error("Failed to clear history:", error);
    alert("Failed to clear history. Please try again.");
  }
};
</script>

<style scoped>
.tabs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.tab {
  flex: 1;
  text-align: center;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-active {
  background-color: hsl(var(--p));
  color: hsl(var(--pc));
}
</style>
